/**
 * MentenantaTab - Fleet Maintenance Management
 * Table view of all cars with maintenance summary, click to see details with 3 sub-tabs.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { toast } from 'sonner';
import {
  Wrench, Plus, Trash2, Search, Calendar, Gauge, ArrowLeft,
  DollarSign, AlertTriangle, Loader2, Pencil, Download, Clock, FileText, X, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  revizie: 'Revizie', schimb_ulei: 'Schimb ulei', itp: 'ITP',
  cauciucuri: 'Cauciucuri', frane: 'Frâne', distributie: 'Distribuție',
  baterie: 'Baterie', ac: 'Aer condiționat', caroserie: 'Caroserie',
  electrica: 'Electrică', suspensie: 'Suspensie', altele: 'Altele',
};

const TYPE_COLORS = {
  revizie: 'bg-blue-100 text-blue-700', schimb_ulei: 'bg-amber-100 text-amber-700',
  itp: 'bg-green-100 text-green-700', cauciucuri: 'bg-slate-100 text-slate-700',
  frane: 'bg-red-100 text-red-700', distributie: 'bg-purple-100 text-purple-700',
  baterie: 'bg-yellow-100 text-yellow-700', ac: 'bg-cyan-100 text-cyan-700',
  caroserie: 'bg-orange-100 text-orange-700', electrica: 'bg-indigo-100 text-indigo-700',
  suspensie: 'bg-pink-100 text-pink-700', altele: 'bg-gray-100 text-gray-700',
};

// ============ Intervention Modal ============
function InterventionModal({ open, onClose, token, car, editIntervention, onSaved }) {
  const [form, setForm] = useState(editIntervention || {
    tip: 'revizie', data: new Date().toISOString().split('T')[0],
    km: '', cost: '', furnizor: '', descriere: '', observatii: ''
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (!editIntervention) fd.append('car_id', car.id);
      fd.append('tip', form.tip);
      fd.append('data', form.data);
      fd.append('km', form.km || '');
      fd.append('cost', form.cost || '');
      fd.append('furnizor', form.furnizor || '');
      fd.append('descriere', form.descriere || '');
      fd.append('observatii', form.observatii || '');
      if (file) fd.append('document', file);
      const url = editIntervention
        ? `${API}/api/mentenanta/interventii/${editIntervention.id}`
        : `${API}/api/mentenanta/interventii`;
      const res = await fetch(url, {
        method: editIntervention ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) {
        toast.success(editIntervention ? 'Intervenție actualizată' : 'Intervenție adăugată');
        onClose(); onSaved();
      } else {
        const e = await res.json(); toast.error(e.detail || 'Eroare');
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editIntervention ? 'Editează Intervenție' : 'Adaugă Intervenție'}</DialogTitle>
          <DialogDescription>{car?.name} ({car?.license_plate})</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="int-tip">Tip intervenție</Label>
            <Select value={form.tip} onValueChange={v => setForm(f => ({ ...f, tip: v }))}>
              <SelectTrigger id="int-tip"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="int-data">Data</Label>
            <Input id="int-data" type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
          </div>
          <div>
            <Label htmlFor="int-km">KM la intervenție</Label>
            <Input id="int-km" type="number" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} placeholder="ex: 148000" data-testid="intervention-km-input" />
          </div>
          <div>
            <Label htmlFor="int-cost">Cost (RON)</Label>
            <Input id="int-cost" type="number" step="0.01" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="ex: 350" data-testid="intervention-cost-input" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="int-furnizor">Service / Furnizor</Label>
            <Input id="int-furnizor" value={form.furnizor} onChange={e => setForm(f => ({ ...f, furnizor: e.target.value }))} placeholder="ex: AutoService SRL" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="int-desc">Descriere</Label>
            <Input id="int-desc" value={form.descriere} onChange={e => setForm(f => ({ ...f, descriere: e.target.value }))} placeholder="ex: Schimb ulei + filtru" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="int-obs">Observații</Label>
            <Input id="int-obs" value={form.observatii} onChange={e => setForm(f => ({ ...f, observatii: e.target.value }))} placeholder="Opțional" />
          </div>
          <div className="col-span-2">
            <Label>Document (factură, deviz)</Label>
            <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} accept=".pdf,.jpg,.jpeg,.png" />
            {editIntervention?.document_filename && !file && (
              <p className="text-xs text-slate-500 mt-1">Document existent: {editIntervention.document_filename}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Renunță</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-intervention-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
            {editIntervention ? 'Salvează' : 'Adaugă'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Schedule Modal ============
function ScheduleModal({ open, onClose, token, car, onSaved }) {
  const [form, setForm] = useState({
    tip: 'revizie', next_date: '', next_km: '', interval_luni: '', interval_km: '', observatii: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('car_id', car.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${API}/api/mentenanta/programari`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) { toast.success('Programare adăugată'); onClose(); onSaved(); }
      else { toast.error('Eroare'); }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adaugă Programare Mentenanță</DialogTitle>
          <DialogDescription>{car?.name} ({car?.license_plate})</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label htmlFor="sch-tip">Tip</Label>
            <Select value={form.tip} onValueChange={v => setForm(f => ({ ...f, tip: v }))}>
              <SelectTrigger id="sch-tip"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="sch-date">Următoarea dată</Label>
            <Input id="sch-date" type="date" value={form.next_date} onChange={e => setForm(f => ({ ...f, next_date: e.target.value }))} />
          </div>
          <div>
            <Label htmlFor="sch-km">La KM</Label>
            <Input id="sch-km" type="number" value={form.next_km} onChange={e => setForm(f => ({ ...f, next_km: e.target.value }))} placeholder="ex: 160000" />
          </div>
          <div>
            <Label htmlFor="sch-int-luni">Repetare (luni)</Label>
            <Input id="sch-int-luni" type="number" value={form.interval_luni} onChange={e => setForm(f => ({ ...f, interval_luni: e.target.value }))} placeholder="ex: 12" />
          </div>
          <div>
            <Label htmlFor="sch-int-km">Repetare (km)</Label>
            <Input id="sch-int-km" type="number" value={form.interval_km} onChange={e => setForm(f => ({ ...f, interval_km: e.target.value }))} placeholder="ex: 15000" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="sch-obs">Observații</Label>
            <Input id="sch-obs" value={form.observatii} onChange={e => setForm(f => ({ ...f, observatii: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Renunță</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />}
            Adaugă
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Deviz Modal ============
function DevizModal({ open, onClose, token, car, editDeviz, onSaved }) {
  const [form, setForm] = useState(editDeviz || {
    data: new Date().toISOString().split('T')[0],
    furnizor: '', descriere: '', suma: '', observatii: ''
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (!editDeviz) fd.append('car_id', car.id);
      fd.append('data', form.data);
      fd.append('furnizor', form.furnizor || '');
      fd.append('descriere', form.descriere || '');
      fd.append('suma', form.suma || '');
      fd.append('observatii', form.observatii || '');
      if (file) fd.append('document', file);
      const url = editDeviz
        ? `${API}/api/mentenanta/devize/${editDeviz.id}`
        : `${API}/api/mentenanta/devize`;
      const res = await fetch(url, {
        method: editDeviz ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) {
        toast.success(editDeviz ? 'Deviz actualizat' : 'Deviz adăugat');
        onClose(); onSaved();
      } else {
        const e = await res.json(); toast.error(e.detail || 'Eroare');
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editDeviz ? 'Editează Deviz' : 'Adaugă Deviz'}</DialogTitle>
          <DialogDescription>{car?.name} ({car?.license_plate})</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="dev-data">Data</Label>
            <Input id="dev-data" type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
          </div>
          <div>
            <Label htmlFor="dev-suma">Sumă (RON)</Label>
            <Input id="dev-suma" type="number" step="0.01" value={form.suma} onChange={e => setForm(f => ({ ...f, suma: e.target.value }))} placeholder="ex: 1500" data-testid="deviz-suma-input" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="dev-furnizor">Service / Furnizor</Label>
            <Input id="dev-furnizor" value={form.furnizor} onChange={e => setForm(f => ({ ...f, furnizor: e.target.value }))} placeholder="ex: AutoService SRL" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="dev-desc">Descriere</Label>
            <Input id="dev-desc" value={form.descriere} onChange={e => setForm(f => ({ ...f, descriere: e.target.value }))} placeholder="ex: Deviz reparație caroserie" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="dev-obs">Observații</Label>
            <Input id="dev-obs" value={form.observatii} onChange={e => setForm(f => ({ ...f, observatii: e.target.value }))} placeholder="Opțional" />
          </div>
          <div className="col-span-2">
            <Label>Document (PDF deviz)</Label>
            <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} accept=".pdf,.jpg,.jpeg,.png" />
            {editDeviz?.document_filename && !file && (
              <p className="text-xs text-slate-500 mt-1">Document existent: {editDeviz.document_filename}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Renunță</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-deviz-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            {editDeviz ? 'Salvează' : 'Adaugă'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Car Detail View ============
function CarDetailView({ car, token, onBack }) {
  const [subTab, setSubTab] = useState('interventii');
  const [interventions, setInterventions] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [devize, setDevize] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showIntModal, setShowIntModal] = useState(false);
  const [editIntervention, setEditIntervention] = useState(null);
  const [showSchModal, setShowSchModal] = useState(false);
  const [showDevModal, setShowDevModal] = useState(false);
  const [editDeviz, setEditDeviz] = useState(null);

  // Export period state
  const [exportDeLa, setExportDeLa] = useState('');
  const [exportPanaLa, setExportPanaLa] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [intRes, schRes, devRes, dashRes, setRes] = await Promise.all([
        fetch(`${API}/api/mentenanta/interventii/${car.id}`, { headers }),
        fetch(`${API}/api/mentenanta/programari/${car.id}`, { headers }),
        fetch(`${API}/api/mentenanta/devize/${car.id}`, { headers }),
        fetch(`${API}/api/mentenanta/dashboard/${car.id}`, { headers }),
        fetch(`${API}/api/mentenanta/setari/${car.id}`, { headers }),
      ]);
      if (intRes.ok) setInterventions((await intRes.json()).interventions || []);
      if (schRes.ok) setSchedules((await schRes.json()).schedules || []);
      if (devRes.ok) setDevize((await devRes.json()).devize || []);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (setRes.ok) setSettings(await setRes.json());
    } catch { toast.error('Eroare la încărcarea datelor'); }
    finally { setLoading(false); }
  }, [car.id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (type, id) => {
    if (!confirm('Sigur vrei să ștergi?')) return;
    const res = await fetch(`${API}/api/mentenanta/${type}/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Șters cu succes'); fetchData(); }
    else toast.error('Eroare la ștergere');
  };

  const handleDownloadDoc = async (type, id) => {
    const res = await fetch(`${API}/api/mentenanta/${type}/${id}/document`, { headers });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const fname = cd.match(/filename="(.+)"/)?.[1] || 'document';
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } else toast.error('Documentul nu a fost găsit');
  };

  const handleExportPdf = async (type, id) => {
    const res = await fetch(`${API}/api/mentenanta/export/${type}/${id}`, { headers });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const fname = cd.match(/filename="(.+)"/)?.[1] || `${type}.pdf`;
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } else toast.error('Eroare la export PDF');
  };

  const handleExportPeriod = async (type) => {
    if (!exportDeLa || !exportPanaLa) { toast.error('Selectează perioada'); return; }
    const res = await fetch(`${API}/api/mentenanta/export/${type}/${car.id}/perioada?de_la=${exportDeLa}&pana_la=${exportPanaLa}`, { headers });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const fname = cd.match(/filename="(.+)"/)?.[1] || `${type}_perioada.pdf`;
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } else toast.error('Eroare la export PDF');
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const fd = new FormData();
      fd.append('interval_revizie_km', settings.interval_revizie_km || '');
      fd.append('interval_revizie_luni', settings.interval_revizie_luni || '');
      fd.append('prag_alerta_warning_km', settings.prag_alerta_warning_km || '');
      fd.append('prag_alerta_high_km', settings.prag_alerta_high_km || '');
      fd.append('consum_normat_ulei', settings.consum_normat_ulei || '');
      fd.append('interval_filtru_km', settings.interval_filtru_km || '');
      const res = await fetch(`${API}/api/mentenanta/setari/${car.id}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) toast.success('Setări salvate');
      else toast.error('Eroare la salvare');
    } finally { setSavingSettings(false); }
  };

  const subTabs = [
    { id: 'interventii', label: 'Intervenții', icon: Wrench, count: interventions.length },
    { id: 'programari', label: 'Programări', icon: Clock, count: schedules.length },
    { id: 'devize', label: 'Devize', icon: FileText, count: devize.length },
    { id: 'setari', label: 'Setări', icon: Settings },
  ];

  return (
    <div className="space-y-4" data-testid="mentenanta-detail">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="mentenanta-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Înapoi la listă
        </Button>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{car.name}</h2>
          <p className="text-sm text-slate-500">{car.license_plate} {dashboard?.current_km ? `• ${dashboard.current_km.toLocaleString()} km` : ''}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Dashboard summary cards */}
          {dashboard && (
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 text-blue-700 mb-1">
                  <Wrench className="w-4 h-4" />
                  <span className="text-xs font-semibold">Intervenții</span>
                </div>
                <p className="text-xl font-bold text-blue-900" data-testid="dashboard-interventions-count">{dashboard.total_interventions}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="flex items-center gap-2 text-amber-700 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-semibold">Cost Intervenții</span>
                </div>
                <p className="text-xl font-bold text-amber-900" data-testid="dashboard-total-cost">{(dashboard.total_cost || 0).toLocaleString('ro-RO')} RON</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                <div className="flex items-center gap-2 text-green-700 mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs font-semibold">Total Devize</span>
                </div>
                <p className="text-xl font-bold text-green-900" data-testid="dashboard-total-devize">{(dashboard.total_devize || 0).toLocaleString('ro-RO')} RON</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                <div className="flex items-center gap-2 text-red-700 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-semibold">Alerte</span>
                </div>
                <p className="text-xl font-bold text-red-900" data-testid="dashboard-alerts-count">{(dashboard.alerts || []).length}</p>
              </div>
            </div>
          )}

          {/* Alerts */}
          {dashboard?.alerts?.length > 0 && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Alerte mentenanță</p>
              {dashboard.alerts.map((a, idx) => (
                <p key={idx} className="text-xs text-red-600">- {TYPE_LABELS[a.tip] || a.tip}: {a.reason}</p>
              ))}
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex gap-1 border-b pb-0">
            {subTabs.map(t => (
              <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`subtab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  subTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />{t.label}
                {t.count !== undefined && <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{t.count}</span>}
              </button>
            ))}
            <div className="flex-1" />
            {subTab === 'interventii' && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => handleExportPeriod('interventii')} data-testid="export-interventii-btn" title="Export PDF perioadă">
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button size="sm" onClick={() => { setEditIntervention(null); setShowIntModal(true); }} data-testid="add-intervention-btn">
                  <Plus className="w-4 h-4 mr-1" />Adaugă intervenție
                </Button>
              </div>
            )}
            {subTab === 'programari' && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => handleExportPeriod('programari')} data-testid="export-programari-btn" title="Export PDF perioadă">
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button size="sm" onClick={() => setShowSchModal(true)} data-testid="add-schedule-btn">
                  <Plus className="w-4 h-4 mr-1" />Adaugă programare
                </Button>
              </div>
            )}
            {subTab === 'devize' && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => handleExportPeriod('devize')} data-testid="export-devize-btn" title="Export PDF perioadă">
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button size="sm" onClick={() => { setEditDeviz(null); setShowDevModal(true); }} data-testid="add-deviz-btn">
                  <Plus className="w-4 h-4 mr-1" />Adaugă deviz
                </Button>
              </div>
            )}
          </div>

          {/* Export period bar - shown for the 3 data tabs */}
          {['interventii', 'programari', 'devize'].includes(subTab) && (
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
              <span className="text-xs font-medium text-slate-600">Export perioadă:</span>
              <Input type="date" value={exportDeLa} onChange={e => setExportDeLa(e.target.value)} className="w-36 h-8 text-xs" data-testid="export-de-la" />
              <span className="text-xs text-slate-400">→</span>
              <Input type="date" value={exportPanaLa} onChange={e => setExportPanaLa(e.target.value)} className="w-36 h-8 text-xs" data-testid="export-pana-la" />
            </div>
          )}

          {/* Interventions list */}
          {subTab === 'interventii' && (
            <div className="space-y-2">
              {interventions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Nicio intervenție înregistrată</p>
              ) : interventions.map(i => (
                <div key={i.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors" data-testid={`intervention-${i.id}`}>
                  <div className={`px-2 py-1 rounded text-xs font-semibold ${TYPE_COLORS[i.tip] || TYPE_COLORS.altele}`}>
                    {TYPE_LABELS[i.tip] || i.tip}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-1">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{i.data}</span>
                      {i.km && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{i.km.toLocaleString()} km</span>}
                      {i.cost > 0 && <span className="flex items-center gap-1 font-semibold text-amber-700"><DollarSign className="w-3 h-3" />{i.cost.toLocaleString('ro-RO')} RON</span>}
                    </div>
                    {i.furnizor && <p className="text-xs text-slate-600">Service: {i.furnizor}</p>}
                    {i.descriere && <p className="text-xs text-slate-500 mt-0.5">{i.descriere}</p>}
                    {i.observatii && <p className="text-xs text-slate-400 italic mt-0.5">{i.observatii}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleExportPdf('interventie', i.id)} title="Export PDF">
                      <FileText className="w-3.5 h-3.5 text-green-600" />
                    </Button>
                    {i.document_filename && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc('interventii', i.id)} title={i.document_filename}>
                        <Download className="w-3.5 h-3.5 text-blue-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditIntervention(i); setShowIntModal(true); }} data-testid={`edit-intervention-${i.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete('interventii', i.id)} data-testid={`delete-intervention-${i.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Schedules list */}
          {subTab === 'programari' && (
            <div className="space-y-2">
              {schedules.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Nicio programare setată</p>
              ) : schedules.map(s => {
                const isOverdue = (s.next_date && s.next_date <= new Date().toISOString().split('T')[0]) ||
                  (s.next_km && dashboard?.current_km >= s.next_km);
                return (
                  <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : ''}`} data-testid={`schedule-${s.id}`}>
                    <div className={`px-2 py-1 rounded text-xs font-semibold ${TYPE_COLORS[s.tip] || TYPE_COLORS.altele}`}>
                      {TYPE_LABELS[s.tip] || s.tip}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 text-xs text-slate-600">
                        {s.next_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Până la: {s.next_date}</span>}
                        {s.next_km && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />La: {s.next_km.toLocaleString()} km</span>}
                      </div>
                      {s.interval_luni && <p className="text-xs text-slate-400 mt-0.5">Repetare: la {s.interval_luni} luni</p>}
                      {s.interval_km && <p className="text-xs text-slate-400 mt-0.5">Repetare: la {s.interval_km.toLocaleString()} km</p>}
                      {s.observatii && <p className="text-xs text-slate-400 italic">{s.observatii}</p>}
                      {isOverdue && <p className="text-xs text-red-600 font-semibold mt-1">Depășită!</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleExportPdf('programare', s.id)} title="Export PDF">
                        <FileText className="w-3.5 h-3.5 text-green-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete('programari', s.id)} data-testid={`delete-schedule-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Devize list */}
          {subTab === 'devize' && (
            <div className="space-y-2">
              {devize.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Niciun deviz înregistrat</p>
              ) : devize.map(d => (
                <div key={d.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors" data-testid={`deviz-${d.id}`}>
                  <div className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700">
                    Deviz
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-1">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{d.data}</span>
                      {d.suma > 0 && <span className="flex items-center gap-1 font-semibold text-green-700"><DollarSign className="w-3 h-3" />{d.suma.toLocaleString('ro-RO')} RON</span>}
                    </div>
                    {d.furnizor && <p className="text-xs text-slate-600">Furnizor: {d.furnizor}</p>}
                    {d.descriere && <p className="text-xs text-slate-500 mt-0.5">{d.descriere}</p>}
                    {d.observatii && <p className="text-xs text-slate-400 italic mt-0.5">{d.observatii}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleExportPdf('deviz', d.id)} title="Export PDF">
                      <FileText className="w-3.5 h-3.5 text-green-600" />
                    </Button>
                    {d.document_filename && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc('devize', d.id)} title={d.document_filename}>
                        <Download className="w-3.5 h-3.5 text-blue-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditDeviz(d); setShowDevModal(true); }} data-testid={`edit-deviz-${d.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete('devize', d.id)} data-testid={`delete-deviz-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Settings tab */}
          {subTab === 'setari' && settings && (
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Wrench className="w-4 h-4" />Intervale Revizie</h3>
                    <div>
                      <Label htmlFor="set-rev-km">Interval revizie (km)</Label>
                      <Input id="set-rev-km" type="number" value={settings.interval_revizie_km || ''} onChange={e => setSettings(s => ({ ...s, interval_revizie_km: e.target.value }))} placeholder="ex: 15000" data-testid="setting-interval-revizie-km" />
                    </div>
                    <div>
                      <Label htmlFor="set-rev-luni">Interval revizie (luni)</Label>
                      <Input id="set-rev-luni" type="number" value={settings.interval_revizie_luni || ''} onChange={e => setSettings(s => ({ ...s, interval_revizie_luni: e.target.value }))} placeholder="ex: 12" data-testid="setting-interval-revizie-luni" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Praguri Alerte</h3>
                    <div>
                      <Label htmlFor="set-warn-km">Alertă galbenă la (km înainte)</Label>
                      <Input id="set-warn-km" type="number" value={settings.prag_alerta_warning_km || ''} onChange={e => setSettings(s => ({ ...s, prag_alerta_warning_km: e.target.value }))} placeholder="ex: 2000" data-testid="setting-prag-warning" />
                    </div>
                    <div>
                      <Label htmlFor="set-high-km">Alertă portocalie la (km înainte)</Label>
                      <Input id="set-high-km" type="number" value={settings.prag_alerta_high_km || ''} onChange={e => setSettings(s => ({ ...s, prag_alerta_high_km: e.target.value }))} placeholder="ex: 1000" data-testid="setting-prag-high" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Gauge className="w-4 h-4" />Consumabile</h3>
                    <div>
                      <Label htmlFor="set-ulei">Consum normat ulei (L/10.000 km)</Label>
                      <Input id="set-ulei" type="number" step="0.1" value={settings.consum_normat_ulei || ''} onChange={e => setSettings(s => ({ ...s, consum_normat_ulei: e.target.value }))} placeholder="ex: 1.5" data-testid="setting-consum-ulei" />
                    </div>
                    <div>
                      <Label htmlFor="set-filtru">Interval schimb filtru (km)</Label>
                      <Input id="set-filtru" type="number" value={settings.interval_filtru_km || ''} onChange={e => setSettings(s => ({ ...s, interval_filtru_km: e.target.value }))} placeholder="ex: 15000" data-testid="setting-interval-filtru" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                    {savingSettings ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Settings className="w-4 h-4 mr-1" />}
                    Salvează setări
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modals */}
      {showIntModal && (
        <InterventionModal open={showIntModal} onClose={() => { setShowIntModal(false); setEditIntervention(null); }}
          token={token} car={car} editIntervention={editIntervention} onSaved={fetchData} />
      )}
      {showSchModal && (
        <ScheduleModal open={showSchModal} onClose={() => setShowSchModal(false)}
          token={token} car={car} onSaved={fetchData} />
      )}
      {showDevModal && (
        <DevizModal open={showDevModal} onClose={() => { setShowDevModal(false); setEditDeviz(null); }}
          token={token} car={car} editDeviz={editDeviz} onSaved={fetchData} />
      )}
    </div>
  );
}

// ============ Main Component — Table View ============
function MentenantaTab({ cars = [], token }) {
  const [selectedCar, setSelectedCar] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [summaries, setSummaries] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSummaries = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await fetch(`${API}/api/mentenanta/sumar`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSummaries(data.summaries || {});
      }
    } catch { /* silent */ }
    finally { setLoadingSummary(false); }
  }, [token]);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);

  const filteredCars = cars.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) ||
      (c.license_plate || '').toLowerCase().includes(q);
  });

  // If a car is selected, show detail view
  if (selectedCar) {
    return (
      <CarDetailView
        car={selectedCar}
        token={token}
        onBack={() => { setSelectedCar(null); fetchSummaries(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="mentenanta-content">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />Mentenanță Flotă</CardTitle>
              <CardDescription>Evidența intervențiilor, programărilor și devizelor per autovehicul</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search bar */}
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Caută după nume sau număr..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white"
                  data-testid="mentenanta-search"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="text-sm text-slate-500">
                Total: <strong>{filteredCars.length}</strong> mașini
              </div>
            </div>
          </div>

          {/* Cars table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume Mașină</TableHead>
                  <TableHead>Nr. Înmatriculare</TableHead>
                  <TableHead>Ultima Intervenție</TableHead>
                  <TableHead>Programări Viitoare</TableHead>
                  <TableHead className="text-right">Devize</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCars.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      Nu există mașini înregistrate
                    </TableCell>
                  </TableRow>
                ) : filteredCars.map(car => {
                  const s = summaries[car.id] || {};
                  return (
                    <TableRow
                      key={car.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelectedCar(car)}
                      data-testid={`mentenanta-car-row-${car.id}`}
                    >
                      <TableCell className="font-medium">{car.name}</TableCell>
                      <TableCell>{car.license_plate || '-'}</TableCell>
                      <TableCell>
                        {s.ultima_interventie ? (
                          <span className="text-sm text-slate-700">{s.ultima_interventie}</span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.next_programare ? (
                          <span className="text-sm text-slate-700">{s.next_programare}</span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.total_devize > 0 ? (
                          <span className="text-sm font-semibold text-green-700">{s.total_devize.toLocaleString('ro-RO')} RON</span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default MentenantaTab;
